import type { CategoryData } from '@openx/types/targeting/category.ts';
import type { TargetingFieldsValues, TargetingReadOnlyProps } from '@openx/types/targeting/targeting.ts';
import { noop } from '@openx/utils/noop.ts';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTargetingDisabledState } from '../../utils/hooks/useTargetingDisabledState.ts';
import { useDropdownOpen } from '../shared/ReadOnlyTargetingDropdown/hooks/useDropdownOpen.ts';
import { StyledReadOnlyTargetingDropdown } from '../shared/ReadOnlyTargetingDropdown/ReadOnlyTargetingDropdown.ts';
import { TargetingReadOnly } from '../shared/TargetingReadOnly/TargetingReadOnly.tsx';
import { CategoryFormPreviewItems } from './CategoryFormPreviewItems.tsx';
import { CategoryItems } from './CategoryItems.tsx';
import { mapCategoryToFormState } from './utils.ts';

type CategoryReadOnlyProps = Omit<TargetingReadOnlyProps<CategoryData>, 'field'> & {
  field: TargetingFieldsValues;
  children?: ReactNode;
};

export const CategoryReadOnly = ({
  name,
  field,
  onOpenDrawerClick,
  onRemoveClick,
  targetingParams,
  readonly,
  dataTest,
  children,
}: CategoryReadOnlyProps) => {
  const { isDisabled, disabledReasonMessage } = useTargetingDisabledState(field);

  const params = useMemo(() => mapCategoryToFormState(targetingParams), [targetingParams]);
  const { t } = useTranslation();

  const hasConfiguredCategory = useMemo(() => {
    const values = Array.from(params.val ?? []);

    return values.some(v => v.trim().length > 0);
  }, [params.val]);

  const optionsCount = hasConfiguredCategory ? 1 : 0;
  const hasValue = optionsCount > 0;

  const { isOpen, toggleOpen } = useDropdownOpen(hasValue);

  const guardedToggleOpen = hasValue ? toggleOpen : noop;

  if (!readonly)
    return (
      <TargetingReadOnly
        name={name}
        field={field}
        isEmpty={!hasConfiguredCategory}
        onOpenDrawerClick={onOpenDrawerClick}
        onRemove={onRemoveClick}
        readonly={readonly}
        isDisabled={isDisabled}
        dataTest={dataTest}
        description={t(
          'Focus campaigns on content categorized according to standardized IAB classifications, helping align ads with relevant topics.'
        )}
        optionsCount={optionsCount}
        isSingleChoice
        isOpen={isOpen}
        toggleOpen={guardedToggleOpen}
        disabledReasonMessage={disabledReasonMessage}
      >
        {children}
        {hasConfiguredCategory && isOpen && (
          <StyledReadOnlyTargetingDropdown>
            <CategoryFormPreviewItems
              targetingParams={params}
              readonly={readonly}
              onOpenDrawerClick={onOpenDrawerClick}
              onRemove={onRemoveClick}
              optionsCount={optionsCount}
              disabledReasonMessage={disabledReasonMessage}
            />
          </StyledReadOnlyTargetingDropdown>
        )}
      </TargetingReadOnly>
    );

  return (
    <TargetingReadOnly
      name={name}
      field={field}
      isEmpty={!hasConfiguredCategory}
      onOpenDrawerClick={onOpenDrawerClick}
      onRemove={onRemoveClick}
      readonly={readonly}
      isDisabled={isDisabled}
      dataTest={dataTest}
      description={t(
        'Focus campaigns on content categorized according to standardized IAB classifications, helping align ads with relevant topics. '
      )}
      disabledReasonMessage={disabledReasonMessage}
    >
      {children}
      {hasConfiguredCategory && <CategoryItems targetingParams={params} />}
    </TargetingReadOnly>
  );
};
